import moment from 'moment';
import { computed, type ComputedRef, type Ref, unref } from 'vue';
import { useActions, useGetters } from 'vuex-composition-helpers';
import { NAMESPACE as DATES_TABS_STORE_NAME } from '@ve/components/hotel/datesTabs/common/store/DatesTabsStore';
import { type IDatesTabsStore } from '@ve/components/hotel/datesTabs/common/store/types/types';
import { NAMESPACE as HOTEL_PAGE_EMPTY_SEARCH_STORE } from '@ve/components/hotel/emptySearch/common/store/hotelPageEmptySearchStore';

type HotelPageEmptySearchStoreUseGetters = {
  isSearchDepartureCityFromRegion: Ref<boolean>;
  isProductsOfSelectedAndOtherDatesAreEmpty: Ref<boolean>;
  isProductsOfSelectedDateAreEmptyButExistsOnOtherDates: Ref<boolean>;
  isSyncProductsExistsOnSelectedDate: Ref<boolean>;
  isSyncProductsOnSelectedDateIncludesTourProducts: Ref<boolean>;
  selectedDateText: Ref<string>;
  otherDatesAvailableInInterval: Ref<Array<string>>;
  isShouldRenderAnotherProductsBlock: Ref<boolean>;
  isShouldRenderEmptySearchBlock: Ref<boolean>;
};

interface UseEmptySearchReturn extends HotelPageEmptySearchStoreUseGetters {
  upperText: ComputedRef<string>;
  clickAnotherSpecificDateHandler: (date: string) => void;
}

const useEmptySearch = (): UseEmptySearchReturn => {
  const { selectDateManually } = useActions<IDatesTabsStore['actions']>(DATES_TABS_STORE_NAME, ['selectDateManually']);

  const {
    isSearchDepartureCityFromRegion,
    isProductsOfSelectedAndOtherDatesAreEmpty,
    isProductsOfSelectedDateAreEmptyButExistsOnOtherDates,
    isSyncProductsExistsOnSelectedDate,
    isSyncProductsOnSelectedDateIncludesTourProducts,
    selectedDateText,
    otherDatesAvailableInInterval,
    isShouldRenderAnotherProductsBlock,
    isShouldRenderEmptySearchBlock,
  } = useGetters<HotelPageEmptySearchStoreUseGetters>(HOTEL_PAGE_EMPTY_SEARCH_STORE, [
    'isSearchDepartureCityFromRegion',
    'isProductsOfSelectedAndOtherDatesAreEmpty',
    'isProductsOfSelectedDateAreEmptyButExistsOnOtherDates',
    'isSyncProductsExistsOnSelectedDate',
    'isSyncProductsOnSelectedDateIncludesTourProducts',
    'selectedDateText',
    'otherDatesAvailableInInterval',
    'isShouldRenderAnotherProductsBlock',
    'isShouldRenderEmptySearchBlock',
  ]);

  const upperText = computed((): string => {
    if (unref(isSyncProductsExistsOnSelectedDate) && !unref(isSyncProductsOnSelectedDateIncludesTourProducts)) {
      return `На ${unref(selectedDateText)} нет туров с авиаперелётами по вашим критериям`;
    }
    return `На ${unref(selectedDateText)} нет предложений по вашим критериям`;
  });

  const clickAnotherSpecificDateHandler = (date: string): void => {
    // Подпись даты не содержит года, а moment подставляет текущий: дата из прошлого
    // означает предложение на следующий год (даты поиска всегда в будущем).
    const parsedDate = moment(date, 'DD MMMM');
    if (parsedDate.isBefore(moment(), 'day')) {
      parsedDate.add(1, 'year');
    }
    selectDateManually(parsedDate.format('YYYY-MM-DD'));
  };

  return {
    isSearchDepartureCityFromRegion,
    isProductsOfSelectedAndOtherDatesAreEmpty,
    isProductsOfSelectedDateAreEmptyButExistsOnOtherDates,
    isSyncProductsExistsOnSelectedDate,
    isSyncProductsOnSelectedDateIncludesTourProducts,
    selectedDateText,
    otherDatesAvailableInInterval,
    isShouldRenderAnotherProductsBlock,
    isShouldRenderEmptySearchBlock,
    upperText,
    clickAnotherSpecificDateHandler,
  };
};

export default useEmptySearch;
